import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import configFile from '../config.js';
import { useAuth } from './AuthContext';
import { securityParamsKey } from '../constants/authorization';
// Stable empty value: MainContent has an effect keyed on securityParams identity.
const NO_PARAMS = {};

const AgentConfigContext = createContext();

export const useAgentConfig = () => {
  const context = useContext(AgentConfigContext);
  if (!context) {
    throw new Error('useAgentConfig must be used within an AgentConfigProvider');
  }
  return context;
};

export const AgentConfigProvider = ({ children }) => {
  const { getAccessToken, loading: authLoading, user } = useAuth();
  const [selectedAgentIndex, setSelectedAgentIndex] = useState(0);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({
    agentId: '',
    name: 'Loading...',
    serviceUrl: configFile.serviceUrl,
    apiUrl: configFile.apiUrl,
  });
  // Security params filled by the user (Postman-style key-value pairs), kept per
  // user and per agent as { [agentId]: params } under the user's own key.
  // Persisted to localStorage so they survive reloads and agent switches;
  // AuthContext.signOut removes the signed-out user's entry.
  const [paramsByAgent, setParamsByAgent] = useState({});
  const securityParams = paramsByAgent[config.agentId] || NO_PARAMS;

  const readStoredParams = (userId) => {
    try {
      return JSON.parse(localStorage.getItem(securityParamsKey(userId))) || {};
    } catch {
      return {};
    }
  };

  // Reload whenever the signed-in user changes, so one user never sees another's.
  useEffect(() => {
    // Single-slot key from older builds; its params are not migrated.
    try {
      localStorage.removeItem('securityParams');
    } catch {
      // Ignore storage errors
    }
    if (!user?.id) {
      setParamsByAgent({});
      return;
    }
    setParamsByAgent(readStoredParams(user.id));
  }, [user?.id]);

  const setSecurityParams = (params) => {
    if (!user?.id) return;
    // Merge into what's stored now, not this tab's snapshot: another tab may
    // have saved a different agent's params since we loaded.
    const next = { ...readStoredParams(user.id), [config.agentId]: params || {} };
    setParamsByAgent(next);
    try {
      localStorage.setItem(securityParamsKey(user.id), JSON.stringify(next));
    } catch {
      // Ignore storage errors
    }
  };

  // Gate on the token itself, not on `user`. On a first login the two do not
  // reliably land in the same render: this effect could fire with `user` set
  // and getAccessToken() still null, sending `Bearer null`, which the backend
  // rejects with a 401 before it even looks up a signing key. The catch below
  // then latches `error`, MainContent renders it as a full-screen
  // "Configuration Error", and — because nothing else changes the deps — only a
  // manual page refresh recovers.
  //
  // A boolean rather than the token string: it flips false -> true exactly once,
  // so a background silent renewal (new token, same `true`) does not refetch the
  // agent list. When it does flip, the effect re-runs and fetchAgents' own
  // setError(null) clears any error from the tokenless attempt.
  const hasToken = Boolean(getAccessToken());

  // Fetch agents from Discover Chat's backend, using the real Keycloak token.
  useEffect(() => {
    if (authLoading || !user || !hasToken) {
      return;
    }

    const fetchAgents = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // canvasApiUrl lives off the bare origin (not /api/v1), same shape as
        // the document-preview URLs in ChatMessage.jsx — reuse the same
        // "strip /api/v1 off apiUrl" trick to get the backend's equivalent.
        const apiOrigin = configFile.apiUrl.replace(/\/api\/v1\/?$/, '');
        const response = await axios.get(`${apiOrigin}/v1/canvas/canvas_info`, {
          headers: {
            'Authorization': `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.data && response.data.code === 0 && response.data.data && response.data.data.length > 0) {
          const agents = response.data.data.map(agent => ({
            agentId: agent.id,
            name: agent.name,
            serviceUrl: configFile.serviceUrl,
            apiUrl: configFile.apiUrl,
          }));

          setAvailableAgents(agents);

          // Load saved agent index from localStorage
          const savedAgentIndex = localStorage.getItem('selectedAgentIndex');
          let indexToSelect = 0;

          if (savedAgentIndex !== null) {
            const index = parseInt(savedAgentIndex, 10);
            if (index >= 0 && index < agents.length) {
              indexToSelect = index;
            }
          }

          setSelectedAgentIndex(indexToSelect);
          setConfig(agents[indexToSelect]);
        } else {
          throw new Error('No agents found or invalid response format');
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
        setError(err.message || 'Failed to fetch agents');
        const fallbackAgent = {
          agentId: '',
          name: 'Error loading agents',
          serviceUrl: configFile.serviceUrl,
          apiUrl: configFile.apiUrl,
        };
        setAvailableAgents([fallbackAgent]);
        setConfig(fallbackAgent);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgents();
    // getAccessToken is not memoized in AuthContext (new reference every render,
    // e.g. on every background silent token renewal) — deliberately excluded
    // here, same as getUserIdDiscover is elsewhere in this codebase (see
    // MainContent.jsx). We only want to refetch when auth actually finishes
    // resolving or the logged-in user changes, not on every token refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, hasToken]);

  const selectAgent = (index) => {
    if (index >= 0 && index < availableAgents.length) {
      setSelectedAgentIndex(index);
      setConfig(availableAgents[index]);
      localStorage.setItem('selectedAgentIndex', index.toString());
    }
  };

  const value = {
    config,
    selectedAgentIndex,
    selectAgent,
    availableAgents,
    isLoading,
    error,
    securityParams,
    setSecurityParams,
  };

  return (
    <AgentConfigContext.Provider value={value}>
      {children}
    </AgentConfigContext.Provider>
  );
};